import css from 'styled-jsx/css';
import {
  space,
  colors,
  radii,
  fonts,
  border,
  fontSizes,
  fontWeight,
  opacity,
  zindex,
} from '@monofront/theme';
import { isMobileApp } from '@utils/environment';

export const variables = css.global`
  :root {
    /* Global */
    --header-layout-max-width: 1440px;
    --layout-max-width: 1200px;
    --layout-margin: 24px;
    --negative-layout-margin: -24px;

    /* Spacers */
    --negative-space-10: -${space[10]}px;
    --negative-space-9: -${space[9]}px;
    --negative-space-8: -${space[8]}px;
    --negative-space-7: -${space[7]}px;
    --negative-space-6: -${space[6]}px;
    --negative-space-5: -${space[5]}px;
    --negative-space-4: -${space[4]}px;
    --negative-space-3: -${space[3]}px;
    --negative-space-2: -${space[2]}px;
    --negative-space-1: -${space[1]}px;
    --space-half: ${space[1] / 2}px;
    --space-1: ${space[1]}px;
    --space-2: ${space[2]}px;
    --space-3: ${space[3]}px;
    --space-4: ${space[4]}px;
    --space-5: ${space[5]}px;
    --space-6: ${space[6]}px;
    --space-7: ${space[7]}px;
    --space-8: ${space[8]}px;
    --space-9: ${space[9]}px;
    --space-10: ${space[10]}px;
    --space-11: ${space[11]}px;

    /* Colors */
    --clr-primary-1: ${colors.primary1};
    --clr-primary-2: ${colors.primary2};
    --clr-primary-3: ${colors.primary3};
    --clr-primary-4: ${colors.primary4};
    --clr-primary-5: ${colors.primary5};

    --clr-secondary-1: ${colors.secondary1};
    --clr-secondary-2: ${colors.secondary2};
    --clr-secondary-3: ${colors.secondary3};
    --clr-secondary-4: ${colors.secondary4};

    --clr-tertiary-1: ${colors.tertiary1};
    --clr-tertiary-2: ${colors.tertiary2};
    --clr-tertiary-3: ${colors.tertiary3};
    --clr-tertiary-5: ${colors.tertiary5};

    --clr-quaternary-1: ${colors.quatertiary1};
    --clr-quaternary-2: ${colors.quatertiary2};
    --clr-quaternary-3: ${colors.quatertiary3};

    --clr-grey-1: ${colors.grey1};
    --clr-grey-2: ${colors.grey2};
    --clr-grey-3: ${colors.grey3};
    --clr-grey-4: ${colors.grey4};
    --clr-grey-5: ${colors.grey5};

    --clr-overlay: ${colors.overlay};

    --clr-danger: ${colors.danger};
    --clr-success: ${colors.success};

    --white: ${colors.white};
    --black: ${colors.black};

    --clr-checkbox-border: ${colors.grey4};
    --clr-checkbox-disabled: ${colors.grey5};
    --border: ${border.primary};
    --border-separator: 1px solid ${colors.borderSeparator};

    /* Border radius */
    --rd-1: ${radii[1]}px;
    --rd-2: ${radii[2]}px;
    --rd-3: ${radii[3]}px;
    --rd-4: ${radii[4]}px;
    --rd-5: ${radii[5]}px;
    --rd-6: ${radii[6]}px;
    --rd-7: ${radii[7]}px;
    --rd-round: ${radii.round};

    /* Typography */
    --font-family-default: ${fonts.default};
    --font-family-body: ${fonts.family2}, ${fonts.default};
    --font-family-1: ${fonts.family1};
    --font-family-2: ${fonts.family2};

    --font-weight-black: ${fontWeight.black};
    --font-weight-bold: ${fontWeight.bold};
    --font-weight-base: ${fontWeight.base};
    --font-weight-light: ${fontWeight.light};

    --font-size-1: ${fontSizes.xsm}px;
    --font-size-1-5: ${fontSizes.sm}px;
    --font-size-2: ${fontSizes.md}px;
    --font-size-2-5: ${fontSizes.xmd}px;
    --font-size-3: ${fontSizes.lg}px;
    --font-size-4: ${fontSizes.xl}px;
    --font-size-5: ${fontSizes['2xl']}px;
    --font-size-6: ${fontSizes['3xl']}px;

    --top-header-banner-height: 28px;

    --mobile-checkout-cta-height: 96px;
    --mobile-checkout-header-height: ${isMobileApp ? '0' : '64px'};

    --desktop-checkout-header-height-collapsed: 80px;
    --desktop-checkout-header-height-expanded: 145px;

    --checkout-header-height: var(--desktop-checkout-header-height-collapsed);

    /* Links */
    --clr-link: ${colors.tertiary1};
    --opacity-link-hover: ${opacity.linkHover};

    /* Layout padding */
    --padding-layout-horizontal-l: 14vw;
    --padding-layout-horizontal-base: 7vw;
    --padding-layout-horizontal-sm: 3.2vw;

    --padding-layout-l: 0 var(--padding-layout-horizontal-l);
    --padding-layout-base: 0 var(--padding-layout-horizontal-base);
    --padding-layout-sm: 0 var(--padding-layout-horizontal-sm);
    --negative-padding-layout-l: 0 calc(-1 * var(--padding-layout-horizontal-l));
    --negative-padding-layout-base: 0
      calc(-1 * var(--padding-layout-horizontal-base));
    --negative-padding-layout-sm: 0
      calc(-1 * var(--padding-layout-horizontal-sm));

    --zindex-base: ${zindex.base};
    --zindex-dropdown: ${zindex.dropdown};
    --zindex-form: ${zindex.dropdown};
    --zindex-sticky: ${zindex.sticky};
    --zindex-fixed: ${zindex.fixed};
    --zindex-modal-backdrop: ${zindex.modalBackdrop};
    --zindex-modal: ${zindex.modal};
    --zindex-popover: ${zindex.popover};
    --zindex-tooltip: ${zindex.tooltip};
    --zindex-productPageCta: ${zindex.productPageCta};
    --zindex-toast: ${zindex.toast};

    /* OLD */
    --text-size-large-h1: 48px;
    --text-size-h1: 32px;
    --text-size-h2: 24px;
    --text-size-h3: 20px;
    --text-size-h4: 18px;
    --text-size-h5: 14px;
    --text-size-h6: 12px;
    --text-size-p: 16px;
  }

  @media (max-width: 800px) {
    :root {
      --mobile-header-minheight: 54px;
      --mobile-header-full-height: 109px;

      --checkout-header-height: var(--mobile-checkout-header-height);

      /* OLD */
      --text-size-h1: 24px;
      --text-size-h2: 20px;
      --text-size-h3: 16px;
    }
  }
`;
